import { ColumnFilter, ColumnSort } from '@tanstack/react-table';
import { VehicleFilterProperty } from 'features/vehicles/companyVehicles';
import { LicensePlatePermission } from 'myParkdUtils/enums';
import {
    DeviceMode,
    ErrorState,
    LocalizedLicensePlate,
    PagingReactTable,
    RegistrationVehicleData,
    UserVehiclesResponse,
    Vehicle,
    VehicleAndDriver,
    WorkingWeek
} from 'types';

export const COMPANY_VEHICLE_CREATE_START = 'COMPANY_VEHICLE_CREATE_START' as const;
export const COMPANY_VEHICLE_CREATE_COMPLETED = 'COMPANY_VEHICLE_CREATE_COMPLETED' as const;
export const COMPANY_VEHICLE_CREATE_ERROR = 'COMPANY_VEHICLE_CREATE_ERROR' as const;

export const COMPANY_VEHICLES_REQUEST_START = 'COMPANY_VEHICLES_REQUEST_START' as const;
export const COMPANY_VEHICLES_REQUEST_COMPLETED = 'COMPANY_VEHICLES_REQUEST_COMPLETED' as const;
export const COMPANY_VEHICLES_REQUEST_ERROR = 'COMPANY_VEHICLES_REQUEST_ERROR' as const;

export const UPDATE_FILTER_PROPERTY = 'UPDATE_FILTER_PROPERTY' as const;
export const REMOVE_DRIVER_AFTER_UNCLAIM = 'REMOVE_DRIVER_AFTER_UNCLAIM' as const;
export const REMOVE_VEHICLE_ROW_AFTER_DELETED = 'REMOVE_VEHICLE_ROW_AFTER_DELETED' as const;

export const UPDATE_DEVICE_MODE = 'UPDATE_DEVICE_MODE' as const;
export const UPDATE_DEVICE_MODE_COMPLETED = 'UPDATE_DEVICE_MODE_COMPLETED' as const;
export const UPDATE_DEVICE_MODE_ERROR = 'UPDATE_DEVICE_MODE_ERROR' as const;

export const UPDATE_LICENSE_PLATE_PERMISSION_COMPLETED = 'UPDATE_LICENSE_PLATE_PERMISSION_COMPLETED' as const;
export const UPDATE_COST_CENTER_COMPLETED = 'UPDATE_COST_CENTER_COMPLETED' as const;

export const UPDATE_LICENSE_PLATE_WORKING_WEEK = 'UPDATE_LICENSE_PLATE_WORKING_WEEK' as const;
export const UPDATE_LICENSE_PLATE_WORKING_WEEK_COMPLETED = 'UPDATE_LICENSE_PLATE_WORKING_WEEK_COMPLETED' as const;
export const UPDATE_LICENSE_PLATE_WORKING_WEEK_ERROR = 'UPDATE_LICENSE_PLATE_WORKING_WEEK_ERROR' as const;

//============================================================================

export const createCompanyVehicle = (vehicle: RegistrationVehicleData) => {
    return {
        type: COMPANY_VEHICLE_CREATE_START,
        payload: { vehicle },
    };
};

export const createCompanyVehicleCompleted = () => {
    return {
        type: COMPANY_VEHICLE_CREATE_COMPLETED,
        payload: {},
    };
};

export const createCompanyVehicleError = (error: ErrorState) => {
    return {
        type: COMPANY_VEHICLE_CREATE_ERROR,
        payload: {},
        error: error,
    };
};

//============================================================================

export const updateCompanyVehicles = (filters: ColumnFilter[], paging: PagingReactTable, sorting: ColumnSort[]) => {
    return {
        type: COMPANY_VEHICLES_REQUEST_START,
        payload: { filters, paging, sorting },
    };
};

export const updateCompanyVehiclesCompleted = (companyVehicles: UserVehiclesResponse) => {
    return {
        type: COMPANY_VEHICLES_REQUEST_COMPLETED,
        payload: { companyVehicles },
    };
};

export const updateCompanyVehiclesError = (error: ErrorState) => {
    return {
        type: COMPANY_VEHICLES_REQUEST_ERROR,
        error: error,
        payload: {},
    };
};

export const updateFilterProperty = (id: VehicleFilterProperty, value: string) => {
    return {
        type: UPDATE_FILTER_PROPERTY,
        payload: { id, value }
    };
};

export const removeDriverAfterUnclaim = (vehicleAndUser: VehicleAndDriver) => {
    return {
        type: REMOVE_DRIVER_AFTER_UNCLAIM,
        payload: { vehicleAndUser },
    };
};

export const removeVehicleRowAfterDeleted = (deletedVehicle: Vehicle) => {
    return {
        type: REMOVE_VEHICLE_ROW_AFTER_DELETED,
        payload: { deletedVehicle },
    };
};

export const updateDeviceMode = (localizedLicensePlate: LocalizedLicensePlate, deviceMode: DeviceMode) => {
    return {
        type: UPDATE_DEVICE_MODE,
        payload: { localizedLicensePlate, deviceMode }
    }
}

export const updateDeviceModeCompleted = (localizedLicensePlate: LocalizedLicensePlate, deviceMode: DeviceMode) => {
    return {
        type: UPDATE_DEVICE_MODE_COMPLETED,
        payload: { localizedLicensePlate, deviceMode }
    }
}

export const updateDeviceModeError = (error: any) => {
    return {
        type: UPDATE_DEVICE_MODE_ERROR,
        error: error,
        payload: {},
    }
}

export const updateLicensePlatePermissionCompleted = (vehicle: Vehicle, permission: LicensePlatePermission) => ({
    type: UPDATE_LICENSE_PLATE_PERMISSION_COMPLETED,
    payload: { vehicle, permission }
} as const)

export const updateCostCenterCompleted = (vehicle: Vehicle, costCenter: string) => ({
    type: UPDATE_COST_CENTER_COMPLETED,
    payload: { vehicle, costCenter }
} as const)


export const updateLicensePlateWorkingWeek = (vehicle: Vehicle, workingWeek: WorkingWeek | undefined) => ({
    type: UPDATE_LICENSE_PLATE_WORKING_WEEK,
    payload: { vehicle, workingWeek }
} as const)

export const updateLicensePlateWorkingWeekCompleted = (vehicle: Vehicle, workingWeek: WorkingWeek | undefined) => ({
    type: UPDATE_LICENSE_PLATE_WORKING_WEEK_COMPLETED,
    payload: { vehicle, workingWeek }
} as const)

export const updateLicensePlateWorkingWeekError = (error: any) => ({
    type: UPDATE_LICENSE_PLATE_WORKING_WEEK_ERROR,
    error: error
} as const)


const Actions = {
    createCompanyVehicle,
    createCompanyVehicleCompleted,
    createCompanyVehicleError,
    updateCompanyVehicles,
    updateCompanyVehiclesCompleted,
    updateCompanyVehiclesError,
    updateFilterProperty,
    removeDriverAfterUnclaim,
    removeVehicleRowAfterDeleted,
    updateDeviceMode,
    updateDeviceModeCompleted,
    updateDeviceModeError,
    updateLicensePlatePermissionCompleted,
    updateCostCenterCompleted,
    updateLicensePlateWorkingWeek,
    updateLicensePlateWorkingWeekCompleted,
    updateLicensePlateWorkingWeekError
};

export type VehiclesAction = ReturnType<(typeof Actions)[keyof typeof Actions]>;